import Icon from "./Icon";
import { sectionMeta, reports, voices, bulletins } from "../data/Knowledge";

const CARDS = [
  {
    meta: sectionMeta.reports,
    count: reports.length,
    blurb:
      "In-depth research reports benchmarking India's competitiveness and charting sector roadmaps.",
    icon: "chart",
  },
  {
    meta: sectionMeta.voices,
    count: voices.length,
    blurb:
      "Advocacy memoranda & position papers distilled from 2,500+ member companies.",
    icon: "users",
  },
  {
    meta: sectionMeta.bulletins,
    count: bulletins.length,
    blurb:
      "Periodical bulletins tracking the economy, trade, regulation and sectors.",
    icon: "doc",
  },
];

export default function HomeKnowledge({ onNavigate }) {
  return (
    <div className="bg-paper">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cream via-blush to-cream" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #221510 1px, transparent 0)",
            backgroundSize: "26px 26px",
          }}
        />
        {/* Floating blobs */}
        <div className="absolute -left-20 top-10 h-72 w-72 rounded-full bg-brand/15 blur-3xl animate-float-slow" />
        <div className="absolute right-0 top-32 h-80 w-80 rounded-full bg-ember/15 blur-3xl animate-float-slower" />

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-cream px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-brand">
              <span className="h-2 w-2 rounded-full bg-brand animate-pulse-dot" />
              The Assocham Knowledge Hub
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] text-ink sm:text-5xl lg:text-6xl">
              Research,{" "}
              <span className="bg-gradient-to-r from-brand to-ember bg-clip-text text-transparent">
                Advocacy
              </span>{" "}
              & Insight for{" "}
              <span className="relative whitespace-nowrap">
                Indian Industry
                <svg
                  className="absolute -bottom-2 left-0 h-3 w-full text-gold"
                  viewBox="0 0 200 12"
                  fill="none"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 9C40 3 160 3 198 9"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink/70">
              A curated library of Assocham reports, industry voice and
              periodical bulletins — click any banner to read the overview and
              download the full document.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() => onNavigate("reports")}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand to-deeprose px-6 py-3.5 text-sm font-bold text-cream shadow-lg shadow-brand/30 transition-transform hover:-translate-y-0.5"
              >
                Explore Reports
                <Icon name="arrow" className="h-4 w-4" />
              </button>
              <button
                onClick={() => onNavigate("about")}
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-cream px-6 py-3.5 text-sm font-bold text-ink transition-colors hover:border-brand/30 hover:text-brand"
              >
                <Icon name="spark" className="h-4 w-4" />
                About Knowledge
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <p className="text-sm font-bold uppercase tracking-wider text-ember">
            Three Knowledge Streams
          </p>
          <h2 className="mt-2 font-display text-3xl font-bold text-ink sm:text-4xl">
            Choose your stream
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {CARDS.map(({ meta, count, blurb, icon }) => (
            <button
              key={meta.key}
              onClick={() => onNavigate(meta.key)}
              className="group relative flex flex-col overflow-hidden rounded-3xl bg-cream p-7 text-left shadow-[0_18px_40px_-28px_rgba(34,21,16,0.4)] ring-1 ring-ink/5 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-30px_rgba(218,33,40,0.45)]"
            >
              <div
                className={`absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${meta.accent} opacity-10 blur-2xl transition-opacity group-hover:opacity-25`}
              />
              <span
                className={`relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${meta.accent} text-cream shadow-lg`}
              >
                <Icon name={icon} className="h-7 w-7" />
              </span>
              <h3 className="relative mt-5 font-display text-2xl font-bold text-ink">
                {meta.label}
              </h3>
              <p className="relative mt-2 text-sm leading-relaxed text-mute">
                {blurb}
              </p>
              <div className="relative mt-5 flex items-center justify-between">
                <span className="text-sm font-bold text-brand">
                  {count} publications
                </span>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ink/5 text-ink transition-all group-hover:bg-brand group-hover:text-cream">
                  <Icon name="arrow" className="h-4 w-4" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Marquee strip */}
      <section className="overflow-hidden border-y border-ink/10 bg-ink py-4">
        <div className="flex w-max animate-marquee gap-12 whitespace-nowrap">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex gap-12">
              {[
                "Industry Research",
                "Policy Advocacy",
                "Economic Intelligence",
                "Sector Roadmaps",
                "Regulatory Decoding",
                "Trade Intelligence",
                "Skill & Employment",
                "Climate & ESG",
              ].map((t) => (
                <span
                  key={t + dup}
                  className="flex items-center gap-12 font-display text-lg font-semibold text-cream/40"
                >
                  {t}
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
